import "zod/compile";
import { z } from "zod";

export const TASK_OUTCOME_FIELD_TYPES = [
  "text",
  "textarea",
  "number",
  "boolean",
  "select",
  "date",
] as const;
export const TASK_OUTCOME_MAX_BYTES = 65536;
const forbiddenKeys = new Set(["constructor", "prototype", "__proto__"]);

const taskOutcomeFieldShape = {
  key: z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z][A-Za-z0-9_]*$/)
    .refine((key) => !forbiddenKeys.has(key), "Reserved field key"),
  label: z.string().trim().min(1).max(200),
  type: z.enum(TASK_OUTCOME_FIELD_TYPES),
  helpText: z.string().max(2000).optional(),
  options: z.array(z.string().trim().min(1).max(200)).max(100).optional(),
  /**
   * Outcomes this field belongs to. Omitted means every outcome (the
   * original shape); when present, the field must be null for others.
   */
  outcomes: z.array(z.string().min(1)).min(1).max(100).optional(),
  requiredFor: z.array(z.string().min(1)).max(100),
};

function validateTaskOutcomeField(
  field: {
    type: (typeof TASK_OUTCOME_FIELD_TYPES)[number];
    options?: string[];
    outcomes?: string[];
    requiredFor: string[];
  },
  ctx: z.RefinementCtx,
): void {
  if (field.type === "select" && !field.options?.length)
    ctx.addIssue({
      code: "custom",
      path: ["options"],
      message: "Select fields need options",
    });
  if (field.type !== "select" && field.options?.length)
    ctx.addIssue({
      code: "custom",
      path: ["options"],
      message: "Only select fields accept options",
    });
  validateUniqueFieldValues(field, ctx);
  const scope = field.outcomes;
  if (scope && field.requiredFor.some((outcome) => !scope.includes(outcome)))
    ctx.addIssue({
      code: "custom",
      path: ["requiredFor"],
      message: "Required outcomes must be among the field's outcomes",
    });
}

/**
 * A result field as pinned into an outcome contract: only what validates
 * and describes the recorded data.
 */
export const TaskOutcomeContractFieldSchema = z
  .strictObject(taskOutcomeFieldShape)
  .superRefine(validateTaskOutcomeField);

/** A result field as configured on a task type, display settings included. */
export const TaskOutcomeFieldSchema = z
  .strictObject({
    ...taskOutcomeFieldShape,
    /**
     * Display only: show the recorded value as a badge wherever the task is
     * listed beside its anchor (e.g. the invoice page task list). Not part
     * of the outcome contract, so toggling it never changes a fingerprint.
     */
    showAsBadge: z.boolean().optional(),
  })
  .superRefine(validateTaskOutcomeField);

function validateUniqueFieldValues(
  field: { options?: string[]; outcomes?: string[]; requiredFor: string[] },
  ctx: z.RefinementCtx,
): void {
  for (const key of ["options", "outcomes", "requiredFor"] as const) {
    const values = field[key] ?? [];
    if (new Set(values).size !== values.length)
      ctx.addIssue({
        code: "custom",
        path: [key],
        message: "Values must be unique",
      });
  }
}

function hasUniqueKeys(fields: readonly { key: string }[]): boolean {
  return new Set(fields.map((field) => field.key)).size === fields.length;
}

export const TaskOutcomeFieldsSchema = z
  .array(TaskOutcomeFieldSchema)
  .max(50)
  .refine(hasUniqueKeys, "Outcome field keys must be unique");
export type TaskOutcomeField = z.infer<typeof TaskOutcomeFieldSchema>;

export const TaskOutcomeContractFieldsSchema = z
  .array(TaskOutcomeContractFieldSchema)
  .max(50)
  .refine(hasUniqueKeys, "Outcome field keys must be unique");
export type TaskOutcomeContractField = z.infer<
  typeof TaskOutcomeContractFieldSchema
>;

/** Whether a field is part of the result for this outcome. */
export function taskOutcomeFieldAppliesTo(
  field: Pick<TaskOutcomeField, "outcomes">,
  outcome: string,
): boolean {
  return !field.outcomes || field.outcomes.includes(outcome);
}

/** The typed result fields recorded with one outcome. */
export function taskOutcomeFieldsForOutcome<
  F extends Pick<TaskOutcomeField, "outcomes">,
>(fields: readonly F[], outcome: string): F[] {
  return fields.filter((field) => taskOutcomeFieldAppliesTo(field, outcome));
}
/** Field definition as it enters an outcome contract: display-only keys dropped. */
export function taskOutcomeContractField(
  field: TaskOutcomeField,
): TaskOutcomeContractField {
  const contractField = { ...field };
  delete contractField.showAsBadge;
  return contractField;
}

export type TaskOutcomeBadge = { key: string; label: string; value: string };

/**
 * Recorded values of the fields flagged `showAsBadge`, in field order. Empty
 * values and fields scoped to other outcomes are skipped.
 */
export function taskOutcomeBadges(
  fields: readonly TaskOutcomeField[],
  outcome: string | null,
  data: TaskOutcomeData | null | undefined,
): TaskOutcomeBadge[] {
  if (!data) return [];
  return fields.flatMap((field) => {
    const value = Object.prototype.hasOwnProperty.call(data, field.key)
      ? data[field.key]
      : null;
    if (
      !field.showAsBadge ||
      value === null ||
      value === "" ||
      (outcome !== null && !taskOutcomeFieldAppliesTo(field, outcome))
    )
      return [];
    return [{ key: field.key, label: field.label, value: String(value) }];
  });
}

/** Shared package targets ES2021 without browser or Node globals. */
function utf8Length(value: string): number {
  let bytes = 0;
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    bytes +=
      codePoint <= 0x7f
        ? 1
        : codePoint <= 0x7ff
          ? 2
          : codePoint <= 0xffff
            ? 3
            : 4;
  }
  return bytes;
}

export const TaskOutcomeDataSchema = z
  .record(
    z.string(),
    z.union([z.string().max(10000), z.number(), z.boolean(), z.null()]),
  )
  .refine(
    (data) => utf8Length(JSON.stringify(data)) <= TASK_OUTCOME_MAX_BYTES,
    "Outcome data exceeds 64 KiB",
  );
export type TaskOutcomeData = z.infer<typeof TaskOutcomeDataSchema>;

export const TASK_AGENT_OUTCOME_STATUSES = [
  "completed",
  "failed",
  "cancelled",
  "needs_input",
  "snoozed",
] as const;

/**
 * Bounds on how long an agent may snooze a task it will retry itself. The
 * floor stops a busy loop; the ceiling stops a task vanishing for weeks.
 */
export const AGENT_SNOOZE_MIN_MINUTES = 5;
export const AGENT_SNOOZE_MAX_MINUTES = 7 * 24 * 60;

const RetryInMinutesSchema = z
  .number()
  .int()
  .min(AGENT_SNOOZE_MIN_MINUTES)
  .max(AGENT_SNOOZE_MAX_MINUTES)
  .nullable()
  // Older stored results and callers predate snooze.
  .default(null);

/**
 * Provider-portable task result shape used by every agent surface. Contract-
 * specific outcome names and fields are applied by
 * {@link buildTaskAgentOutcomeResultSchema} at runtime.
 */
export const TaskAgentOutcomeResultSchema = z.strictObject({
  status: z.enum(TASK_AGENT_OUTCOME_STATUSES),
  outcome: z.string().nullable(),
  summary: z.string().trim().min(1).max(10000),
  fields: TaskOutcomeDataSchema.nullable(),
  assigneeId: z.string().nullable(),
  question: z.string().trim().min(1).max(2000).nullable(),
  attempted: z.string().trim().min(1).max(4000).nullable(),
  retryInMinutes: RetryInMinutesSchema,
});
export type TaskAgentOutcomeResult = z.infer<
  typeof TaskAgentOutcomeResultSchema
>;

export const TaskOutcomeContractSchema = z.strictObject({
  formatVersion: z.literal(1),
  taskTypeId: z.uuid().nullable(),
  outcomeEnumId: z.uuid().nullable(),
  outcomes: z.array(z.string().min(1)),
  outcomeRequired: z.boolean(),
  fields: TaskOutcomeContractFieldsSchema,
  fingerprint: z.string(),
  capturedAt: z.string().nullable(),
});
export type TaskOutcomeContract = z.infer<typeof TaskOutcomeContractSchema>;
export const TaskOutcomeContractResponseSchema = z.object({
  contract: TaskOutcomeContractSchema.nullable(),
  pinned: z.boolean(),
  error: z.string().nullable(),
});

export function taskOutcomeConfigurationErrors(
  fields: readonly TaskOutcomeField[],
  outcomes: readonly string[],
): string[] {
  const errors: string[] = [];
  if (
    !outcomes.length ||
    outcomes.some((name) => !name.trim()) ||
    new Set(outcomes).size !== outcomes.length
  )
    errors.push("Outcome names must be nonempty and unique");
  for (const field of fields) {
    for (const outcome of field.outcomes ?? []) {
      if (!outcomes.includes(outcome))
        errors.push(`${field.key}: unknown outcome '${outcome}'`);
    }
    for (const outcome of field.requiredFor) {
      if (!outcomes.includes(outcome))
        errors.push(`${field.key}: unknown required outcome '${outcome}'`);
    }
  }
  return errors;
}

function fieldValueSchema(field: TaskOutcomeField): z.ZodType {
  switch (field.type) {
    case "number":
      return z.number();
    case "boolean":
      return z.boolean();
    case "date":
      return z.iso.date();
    case "select":
      return z.enum(field.options ?? []);
    case "text":
    case "textarea":
      return z.string().max(10000);
  }
}

function fieldDescription(field: TaskOutcomeField): string {
  const base = field.helpText?.trim() || field.label;
  const scope = field.outcomes
    ? ` Only for outcome ${field.outcomes.map((o) => `'${o}'`).join(", ")}; null otherwise.`
    : "";
  const required = field.requiredFor.length
    ? ` Required for ${field.requiredFor.map((o) => `'${o}'`).join(", ")}.`
    : "";
  return `${base}${scope}${required}`;
}

/** Closed, nullable shape for provider portability. Conditional presence is checked below. */
export function buildTaskOutcomeFieldsSchema(
  fields: readonly TaskOutcomeField[],
) {
  const shape: Record<string, z.ZodType> = {};
  for (const field of fields)
    shape[field.key] = fieldValueSchema(field)
      .nullable()
      .describe(fieldDescription(field));
  return z.strictObject(shape);
}

function unknownOutcomeKeys(
  fields: readonly TaskOutcomeField[],
  input: unknown,
): string[] {
  if (input === null || typeof input !== "object") return [];
  const allowed = new Set(fields.map((field) => field.key));
  return Object.keys(input).filter((key) => !allowed.has(key));
}

export type TaskOutcomeIssue = { path: string; message: string };

function outcomePresenceIssues(
  field: TaskOutcomeField,
  outcome: string,
  value: unknown,
): TaskOutcomeIssue[] {
  if (!taskOutcomeFieldAppliesTo(field, outcome))
    return value === null
      ? []
      : [
          {
            path: field.key,
            message: `Not part of the result for outcome '${outcome}'`,
          },
        ];
  return field.requiredFor.includes(outcome) && value === null
    ? [{ path: field.key, message: `Required for outcome '${outcome}'` }]
    : [];
}
export function validateTaskOutcomeData(
  fields: readonly TaskOutcomeField[],
  outcome: string,
  input: unknown,
):
  | { success: true; data: TaskOutcomeData }
  | { success: false; issues: TaskOutcomeIssue[] } {
  const unknownKeys = unknownOutcomeKeys(fields, input);
  if (unknownKeys.length)
    return {
      success: false,
      issues: unknownKeys.map((path) => ({
        path,
        message: "Unknown outcome field",
      })),
    };
  const raw = z.record(z.string(), z.unknown()).safeParse(input ?? {});
  if (!raw.success)
    return {
      success: false,
      issues: [{ path: "outcomeData", message: "Expected an object" }],
    };
  const normalized: Record<string, unknown> = { ...raw.data };
  for (const field of fields) {
    const value = Object.prototype.hasOwnProperty.call(normalized, field.key)
      ? normalized[field.key]
      : undefined;
    normalized[field.key] =
      typeof value === "string" ? value.trim() || null : (value ?? null);
  }
  const parsed = buildTaskOutcomeFieldsSchema(fields).safeParse(normalized);
  if (!parsed.success)
    return {
      success: false,
      issues: parsed.error.issues.map((issue) => ({
        path: issue.path.join("."),
        message: issue.message,
      })),
    };
  const issues = fields.flatMap((field) =>
    outcomePresenceIssues(field, outcome, parsed.data[field.key]),
  );
  if (issues.length) return { success: false, issues };
  const data = TaskOutcomeDataSchema.safeParse(parsed.data);
  return data.success
    ? { success: true, data: data.data }
    : {
        success: false,
        issues: data.error.issues.map((issue) => ({
          path: issue.path.join("."),
          message: issue.message,
        })),
      };
}

function cancellationIssues(
  outcome: string | null | undefined,
  reason: string | null | undefined,
  data: unknown,
): TaskOutcomeIssue[] {
  const issues: TaskOutcomeIssue[] = reason?.trim()
    ? []
    : [
        {
          path: "outcomeReason",
          message: "A cancellation reason is required",
        },
      ];
  if (outcome != null || data != null)
    issues.push({
      path: "outcomeData",
      message: "Cancellation does not accept an outcome or result data",
    });
  return issues;
}

function verdictIssues(
  contract: TaskOutcomeContract,
  outcome: string | null | undefined,
  reason: string | null | undefined,
): TaskOutcomeIssue[] {
  const issues: TaskOutcomeIssue[] = [];
  if (outcome == null) {
    if (contract.outcomeRequired || contract.fields.length)
      issues.push({ path: "outcome", message: "An outcome is required" });
    else if (reason != null)
      issues.push({
        path: "outcome",
        message: "outcomeReason requires an outcome",
      });
  } else if (!contract.outcomes.includes(outcome))
    issues.push({
      path: "outcome",
      message: `Invalid outcome. Allowed outcomes: ${contract.outcomes.join(", ")}`,
    });
  return issues;
}

/** Shared by the model boundary and deterministic transition service. */
export function taskOutcomeCompletionIssues(
  contract: TaskOutcomeContract,
  status: string,
  outcome: string | null | undefined,
  reason: string | null | undefined,
  data: unknown,
): TaskOutcomeIssue[] {
  if (status === "needs_input") return [];
  if (
    status === "cancelled" &&
    (contract.fields.length || (outcome == null && reason != null))
  ) {
    return cancellationIssues(outcome, reason, data);
  }
  const issues = verdictIssues(contract, outcome, reason);
  if (contract.fields.length) {
    if (!reason?.trim())
      issues.push({
        path: "outcomeReason",
        message: "An outcome reason is required",
      });
    const checked = validateTaskOutcomeData(
      contract.fields,
      outcome ?? "",
      data,
    );
    if (!checked.success) issues.push(...checked.issues);
  } else if (data != null)
    issues.push({
      path: "outcomeData",
      message: "This task does not declare outcome fields",
    });
  return issues;
}

/** Build the strict result schema shown to and enforced for a task-working agent. */
function agentOutcomeReason(
  contract: TaskOutcomeContract,
  outcome: string | null,
  summary: string,
): string | undefined {
  return outcome !== null || contract.fields.length ? summary : undefined;
}

type AgentOutcomeShape = {
  status: (typeof TASK_AGENT_OUTCOME_STATUSES)[number];
  outcome: string | null;
  summary: string;
  fields: Record<string, unknown> | null;
  assigneeId: string | null;
  question: string | null;
  attempted: string | null;
  retryInMinutes: number | null;
};
type AgentOutcomeShapeIssue = { path?: string[]; message: string };

function anySet(values: unknown[]): boolean {
  return values.some((value) => value !== null);
}

function snoozedIssues(value: AgentOutcomeShape): AgentOutcomeShapeIssue[] {
  const issues: AgentOutcomeShapeIssue[] = [];
  if (anySet([value.outcome, value.fields, value.assigneeId, value.question]))
    issues.push({
      message: "Snoozing accepts only a summary, attempted, and retryInMinutes",
    });
  if (value.retryInMinutes === null)
    issues.push({
      path: ["retryInMinutes"],
      message: "retryInMinutes is required to snooze",
    });
  return issues;
}

function needsInputIssues(value: AgentOutcomeShape): AgentOutcomeShapeIssue[] {
  const issues: AgentOutcomeShapeIssue[] = [];
  if (anySet([value.outcome, value.fields]))
    issues.push({
      message: "Use null outcome and fields while waiting for human input",
    });
  if (value.question === null)
    issues.push({
      path: ["question"],
      message: "A specific question is required for needs_input",
    });
  return issues;
}

function cancelledIssues(value: AgentOutcomeShape): AgentOutcomeShapeIssue[] {
  return anySet([
    value.outcome,
    value.fields,
    value.assigneeId,
    value.question,
    value.attempted,
  ])
    ? [
        {
          message: "Cancellation accepts only status and a summary explanation",
        },
      ]
    : [];
}

function concludedIssues(
  contract: TaskOutcomeContract,
  value: AgentOutcomeShape,
): AgentOutcomeShapeIssue[] {
  const issues: AgentOutcomeShapeIssue[] = anySet([
    value.assigneeId,
    value.question,
    value.attempted,
  ])
    ? [
        {
          message:
            "Assignee, question, and attempted are only used for needs_input",
        },
      ]
    : [];
  for (const issue of taskOutcomeCompletionIssues(
    contract,
    value.status,
    value.outcome,
    agentOutcomeReason(contract, value.outcome, value.summary),
    value.fields,
  ))
    issues.push({ path: [issue.path], message: issue.message });
  return issues;
}

/** Which result keys each agent status may carry, beyond the contract. */
function agentOutcomeShapeIssues(
  contract: TaskOutcomeContract,
  value: AgentOutcomeShape,
): AgentOutcomeShapeIssue[] {
  if (value.status === "snoozed") return snoozedIssues(value);
  const retry: AgentOutcomeShapeIssue[] =
    value.retryInMinutes === null
      ? []
      : [
          {
            path: ["retryInMinutes"],
            message: "retryInMinutes is only used for snoozed",
          },
        ];
  if (value.status === "needs_input")
    return [...retry, ...needsInputIssues(value)];
  if (value.status === "cancelled")
    return [...retry, ...cancelledIssues(value)];
  return [...retry, ...concludedIssues(contract, value)];
}

export function buildTaskAgentOutcomeResultSchema(
  contract: TaskOutcomeContract,
) {
  return z
    .strictObject({
      status: z.enum(TASK_AGENT_OUTCOME_STATUSES),
      outcome: z.enum(contract.outcomes).nullable(),
      summary: z.string().trim().min(1).max(10000),
      fields: buildTaskOutcomeFieldsSchema(contract.fields).nullable(),
      assigneeId: z.string().nullable(),
      question: z.string().trim().min(1).max(2000).nullable(),
      attempted: z.string().trim().min(1).max(4000).nullable(),
      retryInMinutes: RetryInMinutesSchema,
    })
    .superRefine((value, ctx) => {
      for (const issue of agentOutcomeShapeIssues(contract, value))
        ctx.addIssue({ code: "custom", ...issue });
    });
}

export const OUTCOME_VALIDATION_FAILED_ACTION_KEY = "outcome_validation_failed";
